// Video.js 10 (@videojs/html custom elements). The <video-player>/<video-skin>
// elements wrap a NATIVE <video>, so playback control and frame stepping talk
// to the real element directly. Sources are appended AV1-first with full codec
// strings — the browser takes the first source it can play, falling back to
// VP9. Step size starts at the manifest fps (23.976) and is refined at runtime
// by measuring mediaTime deltas via requestVideoFrameCallback.
import '@videojs/html/video/player';
import '@videojs/html/video/skin';
import { $ } from './dom';
import { fmtSeconds } from './format';
import { state } from './state';

interface VideoFrameMeta {
  mediaTime: number;
}
type VideoWithRVFC = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, meta: VideoFrameMeta) => void) => number;
};

let video: HTMLVideoElement;
let frameDuration = 1001 / 24000;
let fpsMeasured = false;

const KNOWN_RATES = [24000 / 1001, 24, 25, 30000 / 1001, 30, 48, 50, 60000 / 1001, 60];

function snapToKnownRate(dur: number): number {
  for (const fps of KNOWN_RATES) {
    if (Math.abs(1 / fps - dur) < 0.0015) return 1 / fps;
  }
  return dur;
}

function updateFpsReadout(): void {
  $('#fpsReadout').textContent = `${(1 / frameDuration).toFixed(3)} fps${fpsMeasured ? ' (measured)' : ''}`;
}

export function initPlayer(onTime: (seconds: number) => void): void {
  frameDuration = 1 / state.manifest.fps;
  video = $<HTMLVideoElement>('#video');
  for (const { src, type } of state.manifest.video) {
    const source = document.createElement('source');
    source.src = src;
    source.type = type;
    video.appendChild(source);
  }
  video.load();
  updateFpsReadout();

  const tick = () => {
    $('#timeReadout').textContent = fmtSeconds(video.currentTime);
    onTime(video.currentTime);
  };
  for (const ev of ['timeupdate', 'seeked', 'loadedmetadata']) video.addEventListener(ev, tick);

  // Per-displayed-frame callback: keeps the snapshot pane in sync during
  // playback and measures the real frame duration from mediaTime deltas.
  const rvfc = video as VideoWithRVFC;
  if (rvfc.requestVideoFrameCallback) {
    const deltas: number[] = [];
    let lastMediaTime = -1;
    const loop = (_now: number, meta: VideoFrameMeta) => {
      $('#timeReadout').textContent = fmtSeconds(meta.mediaTime);
      onTime(meta.mediaTime);
      if (!fpsMeasured && !video.paused && video.playbackRate === 1) {
        if (lastMediaTime >= 0) {
          const d = meta.mediaTime - lastMediaTime;
          if (d > 0.008 && d < 0.2) deltas.push(d);
          if (deltas.length >= 24) {
            deltas.sort((a, b) => a - b);
            frameDuration = snapToKnownRate(deltas[Math.floor(deltas.length / 2)]);
            fpsMeasured = true;
            updateFpsReadout();
          }
        }
        lastMediaTime = meta.mediaTime;
      } else {
        lastMediaTime = -1;
      }
      rvfc.requestVideoFrameCallback!(loop);
    };
    rvfc.requestVideoFrameCallback(loop);
  }
}

export function currentTime(): number {
  return video.currentTime;
}

export function duration(): number {
  const d = video.duration;
  return Number.isFinite(d) && d > 0 ? d : state.manifest.duration;
}

/** Seek to seconds (clamped); pauses first so the target frame stays visible. */
export function seekTo(seconds: number, pause = true): void {
  if (pause) video.pause();
  video.currentTime = Math.min(Math.max(seconds, 0), Math.max(duration() - 0.001, 0));
}


/**
 * Step exactly one video frame. Seeks to the *middle* of the neighbor frame
 * so float rounding never lands on a frame boundary.
 */
export function stepFrame(dir: 1 | -1): void {
  video.pause();
  const index = Math.round(video.currentTime / frameDuration - 0.5);
  seekTo((index + dir + 0.5) * frameDuration, false);
}

export function togglePlay(): void {
  if (video.paused) void video.play();
  else video.pause();
}

export function onDurationKnown(cb: () => void): void {
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) cb();
  else video.addEventListener('loadedmetadata', () => cb(), { once: true });
}
